import stripHost from '@vangst/lib/stripHost'
import { useCreateQuizAttempt } from '@vangst/services/oogst/training/attempts'
import { useQuiz } from '@vangst/services/oogst/training/useQuiz'
import { TrainingFragment } from '@vangst/services/oogst/types'
import useViewer from '@vangst/services/oogst/viewer/useViewer'
import { useCallback, useState } from 'react'
import { MdOutlineVerified } from 'react-icons/md'
import Busy from '../../components/feedback/Busy'
import CourseEmptyState from './CourseEmptyState'
import CourseNoAccess from './CourseNoAccess'
import CoursePanel from './CoursePanel'
import CourseResults from './CourseResults'
import DialogCourseFeedback from './feedback/DialogCourseFeedback'

type PropsType = {
  id?: string
  training: TrainingFragment
} & React.HTMLAttributes<HTMLDivElement>

function CourseDetail(props: PropsType) {
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)
  const { training, id } = props
  const { viewer, authenticated } = useViewer()
  const { data, isLoading: isQuizLoading } = useQuiz(
    { id: id || '' },
    { enabled: id != null },
  )
  const quiz = data?.getQuiz
  const emptyQuizState =
    quiz?.currentUserQuizAttempts == null ||
    quiz?.currentUserQuizAttempts?.length === 0
  const { mutate, isLoading } = useCreateQuizAttempt()

  const handleStartQuiz = useCallback(() => {
    mutate({ input: { quizId: quiz?.id || '' } })
  }, [mutate, quiz?.id])

  const current = training?.quizzes?.findIndex((q) => q.id === quiz?.id) + 1
  const total = training?.quizzes?.length
  const quizXofY = `Quiz Part ${current} of ${total}`
  const nextQuizButtonText = `Continue with part ${current + 1} of ${total}`

  if (isQuizLoading || !quiz)
    return (
      <div className="contain-xyc">
        <Busy />
      </div>
    )
  const CourseHeader = () => (
    <div className="flex flex-col">
      <span className="mb-2 block sm:hidden">{quizXofY}</span>
      <div className=" flex flex-row gap-x-3 sm:mb-0 sm:divide-x">
        <span className="min-[1440px]:text-left hidden text-center text-sm sm:block">
          {quizXofY}
        </span>
        <span className="min-[1440px]:text-left text-center text-sm sm:pl-4">
          {quiz?.totalQuestions} Questions
        </span>
        <span className="flex items-center justify-center gap-x-1 pl-2 text-center text-sm sm:pl-4">
          <span className="block sm:mr-2 sm:inline-block md:mr-0 lg:mr-0 xl:mr-2">
            <MdOutlineVerified />
          </span>
          <span>
            {((quiz?.totalCorrectToGainCertification ?? 8) /
              (quiz?.totalQuestions ?? 10)) *
              100}
            % Required
          </span>
        </span>
      </div>
      <h2 className="mt-4 font-600 capitalize heading-sm">{quiz?.title}</h2>
    </div>
  )

  if (authenticated && !viewer?.permissions.trainableUser) {
    return (
      <>
        <CourseHeader />
        <div className="mt-4">
          <CourseNoAccess sessionRedirect={stripHost(training.trainingUrl)} />
        </div>
      </>
    )
  }

  if (emptyQuizState) {
    return (
      <>
        <CourseHeader />
        <div className="mt-4">
          <CourseEmptyState
            handleStartQuiz={handleStartQuiz}
            isLoading={isLoading}
            quiz={quiz}
          />
        </div>
      </>
    )
  }

  if (
    quiz?.mostRecentSubmittedQuizAttempt != null &&
    !quiz?.inProgressQuizAttempt
  ) {
    return (
      <>
        <CourseHeader />

        <div className="mt-4 flex flex-col">
          <CourseResults
            handleStartQuiz={handleStartQuiz}
            quiz={quiz}
            training={training}
            buttonText={nextQuizButtonText}
          />
        </div>
        {showFeedbackModal && training?.currentUserPassed && (
          <DialogCourseFeedback
            email={viewer?.email ?? ''}
            firstName={viewer?.firstName ?? ''}
            handleCancel={() => setShowFeedbackModal(false)}
            trainingName={quiz?.title ?? ''}
          />
        )}
      </>
    )
  }

  return (
    <>
      <CourseHeader />

      <div className="flow-y-xs mt-4">
        <CoursePanel
          quiz={quiz}
          onSettledAction={() => setShowFeedbackModal(true)}
        />
      </div>
    </>
  )
}

export default CourseDetail
